import { useEffect, useRef, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { cmsApi } from './lib/api'
import type { BootstrapData, ScreenPreset } from './types'
import { AppShell, type CmsScreen } from './components/AppShell'
import { AuditPage } from './components/AuditPage'
import { Dashboard } from './components/Dashboard'
import { DuplicatesPage } from './components/DuplicatesPage'
import { EventEditor } from './components/EventEditor'
import { EventList } from './components/EventList'
import { LoadingState } from './components/LoadingState'
import { PlaceEditor } from './components/PlaceEditor'
import { PlacesPage } from './components/PlacesPage'
import { PopularPage } from './components/PopularPage'
import { ProducerEditor } from './components/ProducerEditor'
import { ProducersPage } from './components/ProducersPage'
import { ReviewPage } from './components/ReviewPage'
import { SourcesPage } from './components/SourcesPage'
import { TeamPage } from './components/TeamPage'
import { TaxonomyPage } from './components/TaxonomyPage'
import { Toast, type ToastState } from './components/Toast'

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null)
  const [bootstrapError, setBootstrapError] = useState('')
  const [screen, setScreen] = useState<CmsScreen | 'editor' | 'place-editor' | 'producer-editor'>('dashboard')
  const [eventId, setEventId] = useState<string | null>(null)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [copyId, setCopyId] = useState<string | null>(null)
  // The list the editor returns to: Eventos, or Para revisar for an imported
  // event (PRD.md, item 93).
  const [editorFrom, setEditorFrom] = useState<CmsScreen>('events')
  // The open editor's way out, which asks first when something would be lost
  // (PRD.md, item 73).
  const editorExit = useRef<((go: () => void) => void) | null>(null)
  // Each opening starts the editor afresh, the same event included.
  const [editorKey, setEditorKey] = useState(0)
  const [catalogId, setCatalogId] = useState<string | null>(null)
  // A place or profile begun in Lugares or Perfis (PRD.md, item 98).
  const [catalogDraftId, setCatalogDraftId] = useState<string | null>(null)
  // The filter a list opens with, from the Visão geral (PRD.md, item 101).
  const [preset, setPreset] = useState<ScreenPreset | null>(null)
  const [toast, setToast] = useState<ToastState>(null)

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setAuthReady(true)
    })
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      if (!nextSession) setBootstrap(null)
    })
    return () => data.subscription.unsubscribe()
  }, [])

  const loadBootstrap = async () => {
    setBootstrapError('')
    try {
      setBootstrap(await cmsApi.bootstrap())
    } catch (error) {
      setBootstrapError(error instanceof Error ? error.message : 'Não foi possível abrir o CMS.')
    }
  }

  useEffect(() => {
    if (session) void loadBootstrap()
  }, [session])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 4500)
    return () => window.clearTimeout(timer)
  }, [toast])

  const signIn = async () => {
    setBootstrapError('')
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    })
    if (error) setBootstrapError(error.message)
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setScreen('dashboard')
    setEventId(null)
  }

  const navigate = (target: CmsScreen, filter?: ScreenPreset) => {
    setScreen(target)
    setPreset(filter ?? null)
    setEventId(null)
    setCatalogId(null)
    setCatalogDraftId(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // The menu leaves an editor only after it answers.
  const leave = (target: CmsScreen) => {
    if (['editor', 'place-editor', 'producer-editor', 'taxonomy'].includes(screen) && editorExit.current) editorExit.current(() => navigate(target))
    else navigate(target)
  }

  const openCatalogEditor = (target: 'place-editor' | 'producer-editor', id: string | null, draft: string | null = null) => {
    setCatalogId(id)
    setCatalogDraftId(draft)
    setScreen(target)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // A saved place or producer reaches the event editor's lists at once.
  const catalogSaved = (target: CmsScreen) => async (message: string) => {
    setToast({ type: 'success', message })
    await loadBootstrap()
    navigate(target)
  }

  const openEditor = (id: string | null) => {
    setEventId(id)
    setDraftId(null)
    setCopyId(null)
    setEditorFrom('events')
    setEditorKey((value) => value + 1)
    setScreen('editor')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // A draft continues the creation of a new event.
  const openDraft = (id: string, from: CmsScreen = 'events') => {
    openEditor(null)
    setDraftId(id)
    setEditorFrom(from)
  }

  // A copy starts a new event (PRD.md, item 74).
  const openCopy = (id: string) => {
    openEditor(null)
    setCopyId(id)
  }

  if (!authReady) {
    return <div className="full-screen-state"><LoadingState label="Preparando acesso seguro…" /></div>
  }

  if (!session) {
    return (
      <main className="login-page">
        <section className="login-card">
          <img className="login-logo" src="/guve-icon.png" alt="Guve" width={64} height={64} />
          {/* Only the brand and the way in, as Stripe and Vercel do: a login page
              says nothing about who may enter or what lies behind it. */}
          <h1>Entrar</h1>
          <button className="google-button" onClick={() => void signIn()}>
            <GoogleLogo />
            <span>Continuar com o Google</span>
          </button>
          {bootstrapError && <p className="form-error">{bootstrapError}</p>}
        </section>
      </main>
    )
  }

  if (!bootstrap) {
    return (
      <div className="full-screen-state">
        {bootstrapError ? (
          <section className="login-card">
            <img className="login-logo" src="/guve-icon.png" alt="Guve" width={64} height={64} />
            <h1>Acesso não liberado</h1>
            <p>{bootstrapError}</p>
            <div className="login-card__actions">
              <button className="button button--secondary" onClick={() => void loadBootstrap()}>Tentar novamente</button>
              <button className="text-button text-button--ink" onClick={() => void signOut()}>Usar outra conta</button>
            </div>
          </section>
        ) : <LoadingState label="Carregando o catálogo…" />}
      </div>
    )
  }

  const canEdit = bootstrap.user.role !== 'viewer'
  const catalogEditing = bootstrap.features?.catalog_editing === true

  return (
    <AppShell user={bootstrap.user} categoryCatalogEnabled={bootstrap.features?.category_catalog === true} screen={screen} editorFrom={editorFrom} onNavigate={leave} onSignOut={() => void signOut()}>
      {screen === 'dashboard' && <Dashboard bootstrap={bootstrap} onOpenEvent={openEditor} onViewAll={() => navigate('events')} onNavigate={navigate} />}
      {screen === 'events' && <EventList places={bootstrap.places} categories={bootstrap.categories} onCreate={() => openEditor(null)} onOpenEvent={openEditor} onOpenDraft={openDraft} onDuplicate={openCopy}
        onNotice={(message) => setToast({ type: 'success', message })} onError={(message) => setToast({ type: 'error', message })} />}
      {screen === 'review' && (
        <ReviewPage
          canEdit={canEdit}
          onOpenDraft={(id) => openDraft(id, 'review')}
          onNotice={(message) => setToast({ type: 'success', message })}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      {screen === 'duplicates' && (
        <DuplicatesPage
          canEdit={canEdit}
          catalogEditing={catalogEditing}
          onOpenEvent={openEditor}
          onSaved={(message) => setToast({ type: 'success', message })}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      {screen === 'places' && (
        <PlacesPage
          canEdit={canEdit}
          catalogEditing={catalogEditing}
          initialReview={preset?.placeReview}
          initialProfile={preset?.placeProfile}
          onCreate={() => openCatalogEditor('place-editor', null)}
          onOpenPlace={(id) => openCatalogEditor('place-editor', id)}
          onOpenDraft={(id) => openCatalogEditor('place-editor', null, id)}
          onSaved={(message) => setToast({ type: 'success', message })}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      {screen === 'place-editor' && (
        <PlaceEditor
          key={catalogId ?? catalogDraftId ?? 'new'}
          placeId={catalogId}
          draftId={catalogDraftId}
          exit={editorExit}
          producers={bootstrap.producers}
          canEdit={canEdit}
          catalogEditing={catalogEditing}
          onBack={() => navigate('places')}
          onSaved={catalogSaved('places')}
          onError={(message) => setToast({ type: 'error', message })}
          onNotice={(message) => setToast({ type: 'success', message })}
          onOpenProfile={(id) => openCatalogEditor('producer-editor', id)}
        />
      )}
      {screen === 'producers' && (
        <ProducersPage
          canEdit={canEdit}
          catalogEditing={catalogEditing}
          onCreate={() => openCatalogEditor('producer-editor', null)}
          onOpenProducer={(id) => openCatalogEditor('producer-editor', id)}
          onOpenDraft={(id) => openCatalogEditor('producer-editor', null, id)}
          onNotice={(message) => setToast({ type: 'success', message })}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      {screen === 'producer-editor' && (
        <ProducerEditor
          key={catalogId ?? catalogDraftId ?? 'new'}
          producerId={catalogId}
          draftId={catalogDraftId}
          exit={editorExit}
          places={bootstrap.places}
          canEdit={canEdit}
          catalogEditing={catalogEditing}
          onBack={() => navigate('producers')}
          onSaved={catalogSaved('producers')}
          onError={(message) => setToast({ type: 'error', message })}
          onNotice={(message) => setToast({ type: 'success', message })}
        />
      )}
      {screen === 'sources' && (
        <SourcesPage
          canEdit={canEdit}
          catalogEditing={catalogEditing}
          places={bootstrap.places}
          producers={bootstrap.producers}
          onCatalogChanged={loadBootstrap}
          onSaved={(message) => setToast({ type: 'success', message })}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      {screen === 'popular' && (
        <PopularPage
          canEdit={canEdit}
          initialCity={preset?.popularCity}
          onSaved={(message) => setToast({ type: 'success', message })}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      {screen === 'team' && <TeamPage currentUser={bootstrap.user} />}
      {screen === 'taxonomy' && bootstrap.user.role === 'owner' && <TaxonomyPage exit={editorExit} onChanged={loadBootstrap}
        onSaved={(message) => setToast({ type: 'success', message })} onError={(message) => setToast({ type: 'error', message })} />}
      {screen === 'audit' && <AuditPage user={bootstrap.user} initialActor={preset?.auditActor} onOpenEvent={openEditor} />}
      {screen === 'editor' && (
        <EventEditor
          key={editorKey}
          eventId={eventId}
          draftId={draftId}
          copyFrom={copyId}
          exit={editorExit}
          bootstrap={bootstrap}
          catalogEditing={catalogEditing}
          onNotice={(message) => setToast({ type: 'success', message })}
          onOpenEvent={openEditor}
          onCatalogChanged={loadBootstrap}
          onMerged={async (message) => {
            setToast({ type: 'success', message })
            await loadBootstrap()
            openEditor(eventId)
          }}
          onBack={() => navigate(editorFrom)}
          onSaved={async (message) => {
            setToast({ type: 'success', message })
            await loadBootstrap()
            navigate(editorFrom)
          }}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </AppShell>
  )
}

// Google's own mark, as its sign-in button guidelines ask: the four colours,
// unaltered, on a white button.
function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}
